import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useSnackbar } from 'notistack'
import React, { useContext, useEffect, useState } from 'react'

import { AppContext } from 'providers/context/App'
import { emailVerification } from 'services/User'
import {
  GENERAL_ERROR_MESSAGE,
  SIGNUP_MESSAGES,
} from 'utils/constants/Messages'
import { conditionalClasses, emailVerifier } from 'utils/helper-functions'
import { USER_EMAIL_VERIFY_FAILED } from 'utils/sentry/sentry-messages'
import { captureError } from 'utils/sentry/sentry-utils'
import SignIn from './appointment-signin'
import SignUp from './appointment-signup'

import { ReactComponent as GreenTick } from 'assets/icons/green-tick.svg'

type Props = {
  isMembership?: boolean
}

const SignInSignUp = ({ isMembership }: Props) => {
  const { appMasterData, updateData } = useContext(AppContext)

  useEffect(() => {
    setIsLoggedIn(appMasterData?.isLoggedIn)
  }, [appMasterData])

  const { enqueueSnackbar } = useSnackbar()
  const [isNotExist, setIsNotExist] = useState(false)
  const [isExist, setIsExist] = useState(false)
  const [isShow, setIsShow] = useState(false)
  const [isExpand, setIsExpand] = useState(false)
  const [isBtnShow, setIsBtnShow] = useState(true)
  const [isLoggedIn, setIsLoggedIn] = useState(appMasterData.isLoggedIn)
  const [emailError, setEmailError] = useState('')
  const [email, setEmail] = useState('')

  const expandHandler = () => {
    setIsExpand(!isExpand)
  }

  const verifyEmail = async (e: React.SyntheticEvent) => {
    try {
      e.preventDefault()
      if (validation()) {
        const res = await emailVerification(email)
        const status = res?.customer?.shopify_status
        if (res?.exists?.shopify) {
          updateData({ email: email })
          setEmailError('')
          setIsNotExist(false)

          if (status === 'enabled') {
            setIsNotExist(false)
            setIsExist(true)
          } else if (status === 'invited') {
            setIsNotExist(false)
            setIsExist(false)
            enqueueSnackbar(SIGNUP_MESSAGES.SHOPIFY_STATUS_SIGNUP, {
              variant: 'warning',
            })
          } else if (status === 'disabled') {
            setIsNotExist(false)
            setIsExist(false)
            enqueueSnackbar(SIGNUP_MESSAGES.SHOPIFY_STATUS_DISABLED, {
              variant: 'warning',
            })
          }
        } else {
          setEmailError('')
          updateData({ email: '' })
          setIsNotExist(true)
          setIsExist(false)
        }
        setIsBtnShow(false)
      }
    } catch (error) {
      captureError(USER_EMAIL_VERIFY_FAILED, {
        email,
        error,
        functionName: 'verifyEmail',
      })
      enqueueSnackbar(GENERAL_ERROR_MESSAGE, { variant: 'error' })
    }
  }

  const validation = () => {
    setEmailError(emailVerifier(email) ? '' : SIGNUP_MESSAGES.INVALID_EMAIL)

    return emailVerifier(email)
  }
  const onClose = () => {
    if (appMasterData.isLoggedIn) {
      setIsLoggedIn(true)
      if (appMasterData?.userInfo?.phone_number) {
        setIsShow(false)
        setIsLoggedIn(true)
        setIsExist(false)
        setIsNotExist(false)
      } else {
        setIsExist(true)
        setIsShow(true)
        setIsLoggedIn(false)
      }
    } else {
      setIsLoggedIn(false)
    }
  }

  useEffect(() => {
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appMasterData])

  useEffect(() => {
    if (appMasterData.loginEmail) {
      setEmail(appMasterData.loginEmail)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={conditionalClasses(
        Boolean(isMembership),
        'membership-login-signup-box',
        '',
        'login-signup-box'
      )}
    >
      <Accordion expanded={!isLoggedIn}>
        <AccordionSummary
          expandIcon={isLoggedIn ? <GreenTick /> : null}
          aria-controls="panel1a-content"
          id="panel1a-header"
          onClick={expandHandler}
          className="accordion-summary"
        >
          <div className="payment-head">
            <Chip label="1" />
            Sign In / Sign Up
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <Grid
            container
            className="signup-container"
            size={{
              xs: 12,
            }}
          >
            {!isShow && (
              <Grid className="input-filed-wrapper">
                <FormControl fullWidth variant="filled">
                  <form
                    onSubmit={(e) => {
                      verifyEmail(e).catch((e) => console.error(e))
                    }}
                  >
                    <InputLabel htmlFor="standard-adornment-password">
                      Email Address
                    </InputLabel>
                    <OutlinedInput
                      className="txt-field"
                      id="standard-adornment-password"
                      type="text"
                      value={email}
                      fullWidth
                      error={Boolean(emailError)}
                      onChange={(event) =>
                        setEmail(event?.target.value.toLowerCase())
                      }
                      endAdornment={
                        <InputAdornment position="end" className="txt-link">
                          {isBtnShow && (
                            <Button
                              className="txt-link"
                              onClick={(e) => {
                                verifyEmail(e).catch((e) => console.error(e))
                              }}
                              disabled={!email}
                            >
                              Next
                            </Button>
                          )}
                        </InputAdornment>
                      }
                    />
                    {emailError && (
                      <FormHelperText error={Boolean(emailError)}>
                        {emailError || ''}
                      </FormHelperText>
                    )}
                  </form>
                </FormControl>
              </Grid>
            )}
          </Grid>
          {isNotExist && (
            <SignUp
              email={email}
              setEmailError={(emailError) => setEmailError(emailError)}
              isMembership={Boolean(isMembership)}
            />
          )}
          {(isExist || appMasterData.loginEmail) && (
            <SignIn
              email={email}
              setEmailError={(emailError) => setEmailError(emailError)}
              isMembership={Boolean(isMembership)}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

export default SignInSignUp
